import React, { useEffect, useState, useCallback } from "react";
import { TableEmpty } from "@finance/components/DesignSystem";
import { Navigate } from "react-router-dom";
import { api, fmtNum, fmtDate, errMsg } from "@finance/lib/api";
import { useAuth } from "@finance/context/AuthContext";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { Switch } from "@finance/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@finance/components/ui/tabs";
import { Textarea } from "@finance/components/ui/textarea";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, CheckCircle, DownloadSimple } from "@phosphor-icons/react";
import { BulkUploadButton } from "@finance/components/BulkUpload";
import { exportToCsv } from "@finance/lib/exportCsv";

const TABS = {
  cash: {
    endpoint: "/others/cash",
    label: "Cash Movement",
    columns: [
      { key: "date", label: "Date", type: "date" },
      { key: "description", label: "Description", type: "text" },
      { key: "debit", label: "Debit", type: "number", align: "right" },
      { key: "credit", label: "Credit", type: "number", align: "right" },
      { key: "balance", label: "Balance", type: "number", align: "right", computed: true },
    ],
  },
  directory: {
    endpoint: "/others/directory",
    label: "Directory",
    columns: [
      { key: "name", label: "Name", type: "text" },
      { key: "wallet", label: "Wallet", type: "text" },
      { key: "phone_account", label: "Phone / Account Number", type: "text" },
      { key: "mine", label: "Mine", type: "text" },
    ],
  },
  creditors: {
    endpoint: "/others/creditors",
    label: "Creditors",
    columns: [
      { key: "date", label: "Date", type: "date" },
      { key: "company", label: "Company", type: "text" },
      { key: "exclusive_amount", label: "Exclusive Amount", type: "number", align: "right" },
      { key: "vat", label: "VAT", type: "number", align: "right" },
      { key: "inclusive_amount", label: "Inclusive Amount", type: "number", align: "right", auto: (f) => (Number(f.exclusive_amount) || 0) + (Number(f.vat) || 0) },
      { key: "charge", label: "Charge", type: "text" },
      { key: "verification_code", label: "Verification Code", type: "text" },
      { key: "time_stamp", label: "Time Stamp", type: "text" },
    ],
  },
};

export default function Others() {
  const { user, ready } = useAuth();
  const [tab, setTab] = useState("cash");
  if (ready && user && !user.is_owner) return <Navigate to="/" replace />;
  return (
    <Layout>
      <PageHeader title="Others" subtitle="Private notebook — standalone, not linked to any accounts, VAT or inventory" />
      <div className="p-8">
        <Tabs value={tab} onValueChange={setTab} className="mb-4">
          <TabsList data-testid="others-tabs">
            {Object.entries(TABS).map(([k, c]) => (
              <TabsTrigger key={k} value={k} data-testid={`others-tab-${k}`}>{c.label}</TabsTrigger>
            ))}
            <TabsTrigger value="notes" data-testid="others-tab-notes">Notes</TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === "notes" ? <NotesPanel /> : <CrudTable key={tab} cfg={TABS[tab]} tabKey={tab} />}
      </div>
    </Layout>
  );
}

function blankForm(columns) {
  const f = {};
  columns.forEach((c) => { if (!c.computed) f[c.key] = c.type === "bool" ? false : c.type === "number" ? "" : ""; });
  return f;
}

function CrudTable({ cfg, tabKey }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const editable = cfg.columns.filter((c) => !c.computed);

  const load = useCallback(() => {
    api.get(cfg.endpoint).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [cfg.endpoint]);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditId(null); setForm(blankForm(cfg.columns)); setOpen(true); };
  const openEdit = (row) => {
    const f = {};
    editable.forEach((c) => { f[c.key] = row[c.key] ?? (c.type === "bool" ? false : ""); });
    setEditId(row.id); setForm(f); setOpen(true);
  };

  const save = async () => {
    const payload = {};
    editable.forEach((c) => {
      if (c.auto) payload[c.key] = c.auto(form);
      else if (c.type === "number") payload[c.key] = Number(form[c.key]) || 0;
      else if (c.type === "bool") payload[c.key] = !!form[c.key];
      else payload[c.key] = form[c.key] || null;
    });
    try {
      if (editId) await api.put(`${cfg.endpoint}/${editId}`, payload);
      else await api.post(cfg.endpoint, payload);
      toast.success(editId ? "Row updated" : "Row added");
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this row?")) return;
    try { await api.delete(`${cfg.endpoint}/${id}`); toast.success("Row deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const render = (row, c) => {
    const v = row[c.key];
    if (c.type === "number") return fmtNum(v || 0);
    if (c.type === "date") return v ? fmtDate(v) : "—";
    if (c.type === "bool") return v ? <CheckCircle size={16} weight="fill" className="text-emerald-600" /> : "—";
    return v || "—";
  };

  return (
    <div>
      <div className="mb-3 flex justify-end gap-2">
        <Button variant="outline" data-testid={`others-export-${tabKey}`} onClick={() => exportToCsv(`others-${tabKey}`, rows, cfg.columns.map((c) => ({ key: c.key, label: c.label })))}><DownloadSimple size={16} className="mr-1" />Export</Button>
        <BulkUploadButton basePath="/others" entity={tabKey} title={`Bulk Upload — ${cfg.label}`} onDone={load} />
        <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid={`others-add-${tabKey}`} onClick={openAdd}><Plus size={16} className="mr-1" />Add Row</Button>
      </div>
      <div className="overflow-x-auto rounded-sm border border-gray-200 bg-white" data-testid={`others-table-${tabKey}`}>
        <table className="w-full text-sm">
          <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
            {cfg.columns.map((c) => <th key={c.key} className={`px-3 py-2 ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>)}
            <th className="px-3 py-2 text-right">·</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <TableEmpty colSpan={cfg.columns.length + 1} title="No rows yet" message={`Click "Add Row" to create one.`} />}
            {rows.map((row) => (
              <tr key={row.id} data-testid={`others-row-${tabKey}-${row.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                {cfg.columns.map((c) => (
                  <td key={c.key} className={`px-3 py-2 ${c.align === "right" ? "text-right lx-num" : c.type === "date" || c.type === "number" ? "lx-num" : ""}`}>{render(row, c)}</td>
                ))}
                <td className="px-3 py-2 text-right">
                  <span className="inline-flex items-center gap-2">
                    <button className="text-gray-400 hover:text-[#1B4383]" data-testid={`others-edit-${tabKey}-${row.id}`} onClick={() => openEdit(row)}><PencilSimple size={15} /></button>
                    <button className="text-gray-400 hover:text-red-500" data-testid={`others-del-${tabKey}-${row.id}`} onClick={() => del(row.id)}><Trash size={15} /></button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid={`others-dialog-${tabKey}`}>
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit" : "Add"} — {cfg.label}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {editable.map((c) => (
              <div key={c.key} className={c.type === "bool" ? "col-span-2 flex items-center gap-2" : ""}>
                {c.type === "bool" ? (
                  <>
                    <Switch checked={!!form[c.key]} data-testid={`others-field-${c.key}`} onCheckedChange={(val) => setForm((s) => ({ ...s, [c.key]: val }))} />
                    <Label className="lx-label">{c.label}</Label>
                  </>
                ) : c.auto ? (
                  <>
                    <Label className="lx-label">{c.label} <span className="text-gray-400">(auto)</span></Label>
                    <Input className="mt-1 bg-gray-50 text-gray-600" type="number" readOnly data-testid={`others-field-${c.key}`} value={c.auto(form)} />
                    <p className="mt-0.5 text-[11px] text-gray-400">Auto-filled = Exclusive + VAT</p>
                  </>
                ) : (
                  <>
                    <Label className="lx-label">{c.label}</Label>
                    <Input
                      className="mt-1"
                      type={c.type === "number" ? "number" : c.type === "date" ? "date" : "text"}
                      data-testid={`others-field-${c.key}`}
                      value={form[c.key] ?? ""}
                      onChange={(e) => setForm((s) => ({ ...s, [c.key]: e.target.value }))}
                    />
                  </>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid={`others-save-${tabKey}`} onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function NotesPanel() {
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");
  const load = useCallback(() => {
    api.get("/others/notes").then((r) => setNotes(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!draft.trim()) return;
    try { await api.post("/others/notes", { body: draft.trim() }); setDraft(""); load(); toast.success("Note added"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-3xl" data-testid="others-notes-panel">
      <div className="mb-4 rounded-sm border border-gray-200 bg-white p-3">
        <Textarea data-testid="others-note-draft" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Jot a quick note…" className="min-h-[90px]" />
        <div className="mt-2 flex justify-end">
          <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="others-note-add" onClick={add} disabled={!draft.trim()}><Plus size={16} className="mr-1" />Add Note</Button>
        </div>
      </div>
      {notes.length === 0 && <div className="rounded-sm border border-dashed border-gray-200 p-8 text-center text-sm text-gray-400">No notes yet. Jot your first one above.</div>}
      <div className="space-y-3">
        {notes.map((n) => <NoteCard key={n.id} note={n} onChanged={load} />)}
      </div>
    </div>
  );
}

function NoteCard({ note, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note.body || "");
  const save = async () => {
    try { await api.put(`/others/notes/${note.id}`, { body: text }); setEditing(false); onChanged(); toast.success("Note updated"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm("Delete this note?")) return;
    try { await api.delete(`/others/notes/${note.id}`); onChanged(); toast.success("Note deleted"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-sm border border-gray-200 bg-white p-3" data-testid={`others-note-${note.id}`}>
      {editing ? (
        <>
          <Textarea data-testid={`others-note-edit-${note.id}`} value={text} onChange={(e) => setText(e.target.value)} className="min-h-[80px]" />
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => { setEditing(false); setText(note.body || ""); }}>Cancel</Button>
            <Button size="sm" className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid={`others-note-save-${note.id}`} onClick={save}>Save</Button>
          </div>
        </>
      ) : (
        <>
          <div className="whitespace-pre-wrap text-sm text-gray-800">{note.body}</div>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[11px] text-gray-400">{fmtDate(note.updated_at || note.created_at)}</span>
            <span className="inline-flex gap-2">
              <button className="text-gray-400 hover:text-[#1B4383]" data-testid={`others-note-editbtn-${note.id}`} onClick={() => setEditing(true)}><PencilSimple size={15} /></button>
              <button className="text-gray-400 hover:text-red-500" data-testid={`others-note-del-${note.id}`} onClick={del}><Trash size={15} /></button>
            </span>
          </div>
        </>
      )}
    </div>
  );
}
